import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';
import DashboardLayout from '../components/DashboardLayout';
import { api } from '../api/client';
import type { VenueStatus } from '../types';

interface VenueListItem {
  id: string;
  name: string;
  slug: string;
  regionId: string | null;
  city: string;
  state: string;
  isPublished: boolean;
  venueMode: VenueStatus;
  paymentRouting: 'connect' | 'platform';
  availabilityStatusOverride: 'available' | 'unavailable' | 'unknown' | null;
  stripeStatus: 'pending' | 'restricted' | 'completed' | null;
  stripeChargesEnabled: boolean;
  stripePayoutsEnabled: boolean;
  commissionBps: number | null;
  businessTierId: number | null;
  businessTierName: string | null;
  businessTierColor: string | null;
  msaStatus: 'generated' | 'sent' | 'signed' | null;
  msaSignedAt: string | null;
  venueLegalName: string | null;
  venueContactName: string | null;
  venueContactEmail: string | null;
  createdAt: string;
}

interface RegionOption {
  id: string;
  name: string;
  slug: string;
  state: string;
}

interface TierOption {
  id: number;
  name: string;
  display_name: string;
  color: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// Lock icon SVGs
const LockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0110 0v4" />
  </svg>
);

const UnlockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 019.9-1" />
  </svg>
);

const DocIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);

export default function SuperAdminScreen() {
  const { switchVenue, isSuperAdmin } = useAuth();
  const { showConfirm, showAlert } = useModal();
  const selectedVenueId = api.getSelectedVenueId();

  const [venues, setVenues] = useState<VenueListItem[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [regions, setRegions] = useState<RegionOption[]>([]);
  const [tiers, setTiers] = useState<TierOption[]>([]);
  const [switching, setSwitching] = useState<string | null>(null);
  const [updatingRegion, setUpdatingRegion] = useState<string | null>(null);
  const [updatingPublished, setUpdatingPublished] = useState<string | null>(null);
  const [updatingAvailability, setUpdatingAvailability] = useState<string | null>(null);
  const [updatingTier, setUpdatingTier] = useState<string | null>(null);
  const [updatingCommission, setUpdatingCommission] = useState<string | null>(null);
  const [generatingDocs, setGeneratingDocs] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Commission lock state: which venue IDs are unlocked
  const [unlockedCommissions, setUnlockedCommissions] = useState<Set<string>>(new Set());
  // Temporary commission input values
  const [commissionInputs, setCommissionInputs] = useState<Record<string, string>>({});

  const isBusy = (venueId: string) =>
    updatingRegion === venueId || updatingPublished === venueId ||
    updatingAvailability === venueId ||
    updatingTier === venueId || updatingCommission === venueId ||
    generatingDocs === venueId || switching !== null;

  const fetchVenues = useCallback(async (page = 1, searchTerm = '') => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getSuperadminVenues({ search: searchTerm, page, limit: 20 });
      setVenues(response.venues as any);
      setPagination(response.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load venues');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchVenues(1, search);
    }
  }, [isSuperAdmin, fetchVenues]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    (async () => {
      try {
        const [allRegions, tiersResp] = await Promise.all([
          api.getRegions(),
          api.getBusinessTiers(),
        ]);
        setRegions(allRegions);
        setTiers(tiersResp.tiers);
      } catch {
        setRegions([]);
        setTiers([]);
      }
    })();
  }, [isSuperAdmin]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchVenues(1, search);
  };

  const handlePageChange = (newPage: number) => {
    fetchVenues(newPage, search);
  };

  const handleSwitchVenue = async (venue: VenueListItem) => {
    if (switching) return;
    const confirmed = await showConfirm(
      'Switch Venue',
      `Switch to "${venue.name}"? The page will reload with the new venue's data.`,
      { confirmLabel: 'Switch', type: 'info' }
    );
    if (!confirmed) return;
    try {
      setSwitching(venue.id);
      await switchVenue(venue.id, venue.name);
    } catch (err) {
      await showAlert('Error', err instanceof Error ? err.message : 'Failed to switch venue', 'error');
      setSwitching(null);
    }
  };

  const handleRegionChange = async (venue: VenueListItem, nextRegionId: string) => {
    if (isBusy(venue.id)) return;
    if (!nextRegionId || venue.regionId === nextRegionId) return;
    const nextRegion = regions.find(r => r.id === nextRegionId);
    const confirmed = await showConfirm(
      'Update Venue Region',
      `Set "${venue.name}" region to "${nextRegion?.name || 'Selected region'}"?`,
      { confirmLabel: 'Update', type: 'warning' }
    );
    if (!confirmed) return;
    const prevVenues = venues;
    setUpdatingRegion(venue.id);
    setVenues(current => current.map(v => v.id === venue.id ? { ...v, regionId: nextRegionId } : v));
    try {
      await api.setSuperadminVenueRegion(venue.id, nextRegionId);
    } catch (err) {
      setVenues(prevVenues);
      await showAlert('Error', err instanceof Error ? err.message : 'Failed to update region', 'error');
    } finally {
      setUpdatingRegion(null);
    }
  };

  const handleTogglePublished = async (venue: VenueListItem) => {
    if (isBusy(venue.id)) return;
    const nextValue = !venue.isPublished;
    const confirmed = await showConfirm(
      nextValue ? 'Publish Venue' : 'Unpublish Venue',
      nextValue
        ? `Publish "${venue.name}" so it appears in the consumer app?`
        : `Unpublish "${venue.name}" so it no longer appears in the consumer app?`,
      { confirmLabel: nextValue ? 'Publish' : 'Unpublish', type: 'warning' }
    );
    if (!confirmed) return;
    const prevVenues = venues;
    setUpdatingPublished(venue.id);
    setVenues(current => current.map(v => v.id === venue.id ? { ...v, isPublished: nextValue } : v));
    try {
      await api.setSuperadminVenuePublished(venue.id, nextValue);
    } catch (err) {
      setVenues(prevVenues);
      await showAlert('Error', err instanceof Error ? err.message : 'Failed to update published status', 'error');
    } finally {
      setUpdatingPublished(null);
    }
  };

  const handleAvailabilityOverrideChange = async (
    venue: VenueListItem,
    nextValue: 'available' | 'unavailable' | 'unknown' | null
  ) => {
    if (isBusy(venue.id)) return;
    if (venue.availabilityStatusOverride === nextValue) return;
    const label = nextValue === null ? 'Auto' : nextValue;
    const confirmed = await showConfirm(
      'Update Map Availability',
      `Set "${venue.name}" map availability override to "${label}"?`,
      { confirmLabel: 'Update', type: 'warning' }
    );
    if (!confirmed) return;
    const prevVenues = venues;
    setUpdatingAvailability(venue.id);
    setVenues(current => current.map(v => v.id === venue.id ? { ...v, availabilityStatusOverride: nextValue } : v));
    try {
      await api.setSuperadminVenueAvailabilityOverride(venue.id, nextValue);
    } catch (err) {
      setVenues(prevVenues);
      await showAlert('Error', err instanceof Error ? err.message : 'Failed to update map availability override', 'error');
    } finally {
      setUpdatingAvailability(null);
    }
  };

  const handleTierChange = async (venue: VenueListItem, nextTierId: string) => {
    if (isBusy(venue.id)) return;
    const tierId = nextTierId ? parseInt(nextTierId) : null;
    if (venue.businessTierId === tierId) return;
    const tier = tiers.find(t => t.id === tierId);
    const confirmed = await showConfirm(
      'Update Business Tier',
      `Set "${venue.name}" tier to "${tier?.display_name || 'None'}"?`,
      { confirmLabel: 'Update', type: 'warning' }
    );
    if (!confirmed) return;
    setUpdatingTier(venue.id);
    try {
      await api.setVenueTier(venue.id, tierId);
      setVenues(current => current.map(v => v.id === venue.id ? {
        ...v, businessTierId: tierId,
        businessTierName: tier?.display_name || null,
        businessTierColor: tier?.color || null,
      } : v));
    } catch (err) {
      await showAlert('Error', err instanceof Error ? err.message : 'Failed to update tier', 'error');
    } finally {
      setUpdatingTier(null);
    }
  };

  const toggleCommissionLock = (venueId: string) => {
    setUnlockedCommissions(prev => {
      const next = new Set(prev);
      if (next.has(venueId)) {
        next.delete(venueId);
        // Clear the input
        setCommissionInputs(cur => { const c = { ...cur }; delete c[venueId]; return c; });
      } else {
        next.add(venueId);
        // Initialize input with current value
        const venue = venues.find(v => v.id === venueId);
        const pct = venue?.commissionBps != null ? (venue.commissionBps / 100).toString() : '';
        setCommissionInputs(cur => ({ ...cur, [venueId]: pct }));
      }
      return next;
    });
  };

  const handleCommissionSave = async (venue: VenueListItem) => {
    const inputVal = commissionInputs[venue.id];
    const pct = parseFloat(inputVal);
    if (isNaN(pct) || pct < 0 || pct > 100) {
      await showAlert('Invalid Rate', 'Commission must be between 0% and 100%', 'error');
      return;
    }
    const bps = Math.round(pct * 100);
    if (bps === venue.commissionBps) {
      toggleCommissionLock(venue.id);
      return;
    }
    const confirmed = await showConfirm(
      'Update Commission Rate',
      `Set "${venue.name}" commission to ${pct}%?`,
      { confirmLabel: 'Update', type: 'warning' }
    );
    if (!confirmed) return;
    setUpdatingCommission(venue.id);
    try {
      await api.setVenueCommission(venue.id, bps);
      setVenues(current => current.map(v => v.id === venue.id ? { ...v, commissionBps: bps } : v));
      toggleCommissionLock(venue.id);
    } catch (err) {
      await showAlert('Error', err instanceof Error ? err.message : 'Failed to update commission', 'error');
    } finally {
      setUpdatingCommission(null);
    }
  };

  // Generate docs modal state
  const [docFormVenue, setDocFormVenue] = useState<VenueListItem | null>(null);
  const [docFormData, setDocFormData] = useState({
    legalName: '',
    contactName: '',
    contactEmail: '',
    displayName: '',
    address: '',
    city: '',
    state: '',
    commissionBps: '',
  });
  const [docFormSubmitting, setDocFormSubmitting] = useState(false);
  const [docFormError, setDocFormError] = useState<string | null>(null);

  const openDocForm = (venue: VenueListItem) => {
    if (isBusy(venue.id)) return;
    if (venue.msaStatus === 'signed') {
      showAlert('Already Signed', `"${venue.name}" already has signed documents.`, 'info');
      return;
    }
    setDocFormData({
      legalName: venue.venueLegalName || venue.name,
      contactName: venue.venueContactName || '',
      contactEmail: venue.venueContactEmail || '',
      displayName: venue.name,
      address: [venue.city, venue.state].filter(Boolean).join(', ') || '',
      city: venue.city || '',
      state: venue.state || '',
      commissionBps: venue.commissionBps != null ? String(venue.commissionBps / 100) : '',
    });
    setDocFormError(null);
    setDocFormVenue(venue);
  };

  const submitDocForm = async () => {
    if (!docFormVenue) return;
    if (!docFormData.contactName.trim()) { setDocFormError('Contact name is required'); return; }
    if (!docFormData.contactEmail.trim()) { setDocFormError('Contact email is required'); return; }
    setDocFormSubmitting(true);
    setDocFormError(null);
    try {
      await api.generateVenueDocs(docFormVenue.id, {
        contactName: docFormData.contactName.trim(),
        contactEmail: docFormData.contactEmail.trim(),
        legalName: docFormData.legalName.trim() || undefined,
      });
      setVenues(current => current.map(v => v.id === docFormVenue.id ? {
        ...v,
        msaStatus: 'generated' as const,
        msaSignedAt: null,
        venueContactName: docFormData.contactName.trim(),
        venueContactEmail: docFormData.contactEmail.trim(),
        venueLegalName: docFormData.legalName.trim() || v.venueLegalName,
      } : v));
      setDocFormVenue(null);
      await showAlert('Documents Generated', `MSA documents for "${docFormVenue.name}" have been generated successfully.`, 'success');
    } catch (err: any) {
      setDocFormError(err?.message || 'Failed to generate documents');
    } finally {
      setDocFormSubmitting(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <DashboardLayout>
        <div className="page-header">
          <h1>Access Denied</h1>
          <p>This page is only available to Super Administrators.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="page-header">
        <div>
          <h1>All Venues</h1>
          <p>Super Admin venue management and switching</p>
        </div>
      </div>

      {/* Search */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: '12px' }}>
          <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
            <input
              type="text"
              placeholder="Search by name, slug, or city..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
          <button type="submit" className="btn btn-primary">Search</button>
          {search && (
            <button type="button" className="btn btn-secondary" onClick={() => { setSearch(''); fetchVenues(1, ''); }}>
              Clear
            </button>
          )}
        </form>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: '24px' }}>{error}</div>}

      {/* Venues Table */}
      <div className="card" style={{ overflow: 'auto' }}>
        {loading ? (
          <div className="loading" style={{ minHeight: '200px' }}><div className="spinner"></div></div>
        ) : venues.length === 0 ? (
          <div className="empty-state"><h3>No venues found</h3><p>Try adjusting your search criteria</p></div>
        ) : (
          <>
            <table className="pricing-table" style={{ width: '100%', minWidth: '1400px' }}>
              <thead>
                <tr>
                  <th>Venue Name</th>
                  <th>Tier</th>
                  <th>Commission</th>
                  <th>Region</th>
                  <th>Status</th>
                  <th>Booking</th>
                  <th>Payments</th>
                  <th>Availability</th>
                  <th>Stripe</th>
                  <th>MSA</th>
                  <th style={{ width: '120px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {venues.map((venue) => (
                  <tr key={venue.id} style={{
                    background: selectedVenueId === venue.id ? 'var(--primary-light)' : undefined
                  }}>
                    {/* Venue Name */}
                    <td>
                      <div>
                        <strong>{venue.name}</strong>
                        <div style={{ fontSize: '12px', color: 'var(--gray-500)' }}>{venue.city}, {venue.state}</div>
                      </div>
                    </td>

                    {/* Tier */}
                    <td>
                      <select
                        value={venue.businessTierId ?? ''}
                        onChange={(e) => handleTierChange(venue, e.target.value)}
                        disabled={isBusy(venue.id)}
                        style={{
                          minWidth: '110px',
                          fontSize: '12px',
                          padding: '4px 8px',
                          borderColor: venue.businessTierColor || undefined,
                          color: venue.businessTierColor || undefined,
                          fontWeight: venue.businessTierId ? 600 : 400,
                        }}
                      >
                        <option value="">No tier</option>
                        {tiers.map(t => (
                          <option key={t.id} value={t.id}>{t.display_name}</option>
                        ))}
                      </select>
                    </td>

                    {/* Commission with lock */}
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {unlockedCommissions.has(venue.id) ? (
                          <>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="1"
                              value={commissionInputs[venue.id] ?? ''}
                              onChange={(e) => setCommissionInputs(cur => ({ ...cur, [venue.id]: e.target.value }))}
                              onKeyDown={(e) => { if (e.key === 'Enter') handleCommissionSave(venue); if (e.key === 'Escape') toggleCommissionLock(venue.id); }}
                              style={{ width: '60px', fontSize: '12px', padding: '4px 6px', textAlign: 'right' }}
                              autoFocus
                              disabled={updatingCommission === venue.id}
                            />
                            <span style={{ fontSize: '12px', color: 'var(--gray-500)' }}>%</span>
                            <button
                              onClick={() => handleCommissionSave(venue)}
                              disabled={updatingCommission === venue.id}
                              style={{
                                background: 'none', border: 'none', cursor: 'pointer',
                                color: 'var(--success)', padding: '2px', display: 'flex',
                              }}
                              title="Save"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            </button>
                            <button
                              onClick={() => toggleCommissionLock(venue.id)}
                              style={{
                                background: 'none', border: 'none', cursor: 'pointer',
                                color: 'var(--gray-400)', padding: '2px', display: 'flex',
                              }}
                              title="Cancel"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                              </svg>
                            </button>
                          </>
                        ) : (
                          <>
                            <span style={{
                              fontSize: '13px', fontWeight: 600,
                              color: venue.commissionBps === 0 ? '#15803d' : 'var(--gray-900)',
                            }}>
                              {venue.commissionBps != null ? `${(venue.commissionBps / 100)}%` : '—'}
                            </span>
                            {venue.commissionBps === 0 && (
                              <span style={{
                                fontSize: '10px', fontWeight: 600, padding: '1px 5px',
                                borderRadius: '3px', background: '#dcfce7', color: '#15803d',
                              }}>FREE</span>
                            )}
                            <button
                              onClick={() => toggleCommissionLock(venue.id)}
                              disabled={isBusy(venue.id)}
                              style={{
                                background: 'none', border: 'none', cursor: 'pointer',
                                color: 'var(--gray-400)', padding: '2px', display: 'flex',
                                opacity: isBusy(venue.id) ? 0.3 : 1,
                              }}
                              title="Unlock to edit commission rate"
                            >
                              <LockIcon />
                            </button>
                          </>
                        )}
                      </div>
                    </td>

                    {/* Region */}
                    <td>
                      <select
                        value={venue.regionId ?? ''}
                        onChange={(e) => handleRegionChange(venue, e.target.value)}
                        disabled={isBusy(venue.id)}
                        style={{ minWidth: '140px', fontSize: '12px', padding: '4px 8px' }}
                      >
                        <option value="" disabled>Select region</option>
                        {regions.map((region) => (
                          <option key={region.id} value={region.id}>{region.name} ({region.state})</option>
                        ))}
                      </select>
                    </td>

                    {/* Published Status */}
                    <td>
                      <label className="toggle" style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                        <input type="checkbox" checked={venue.isPublished} onChange={() => handleTogglePublished(venue)} disabled={isBusy(venue.id)} />
                        <span className="toggle-switch" />
                        <span style={{ fontSize: '11px', color: venue.isPublished ? 'var(--success)' : 'var(--gray-500)', fontWeight: 600 }}>
                          {venue.isPublished ? 'Live' : 'Draft'}
                        </span>
                      </label>
                    </td>

                    {/* Native venue mode */}
                    <td>
                      <span className="status-badge status-badge--active">Native</span>
                    </td>

                    {/* Native bookings always use the venue's connected account. */}
                    <td>
                      <span style={{ fontSize: '12px', fontWeight: 600 }}>Stripe Connect</span>
                    </td>

                    {/* Map Availability */}
                    <td>
                      <select
                        value={venue.availabilityStatusOverride ?? ''}
                        onChange={(e) => handleAvailabilityOverrideChange(venue, (e.target.value || null) as any)}
                        disabled={isBusy(venue.id)}
                        style={{ minWidth: '90px', fontSize: '12px', padding: '4px 8px' }}
                      >
                        <option value="">Auto</option>
                        <option value="available">Available</option>
                        <option value="unavailable">Unavailable</option>
                        <option value="unknown">Unknown</option>
                      </select>
                    </td>

                    {/* Stripe */}
                    <td>
                      {venue.stripeStatus === null ? (
                        <span style={{ fontSize: '12px', color: 'var(--gray-400)' }}>—</span>
                      ) : (
                        <span style={{
                          fontSize: '10px', fontWeight: 600, padding: '2px 6px', borderRadius: '4px',
                          background: venue.stripeStatus === 'completed' ? '#dcfce7' : venue.stripeStatus === 'restricted' ? '#fef3c7' : 'var(--gray-100)',
                          color: venue.stripeStatus === 'completed' ? '#15803d' : venue.stripeStatus === 'restricted' ? '#d97706' : 'var(--gray-600)',
                        }}>
                          {venue.stripeStatus === 'completed' ? 'Active' : venue.stripeStatus === 'restricted' ? 'Restricted' : 'Pending'}
                        </span>
                      )}
                    </td>

                    {/* MSA / Generate Docs */}
                    <td>
                      {venue.msaStatus === 'signed' ? (
                        <span style={{
                          fontSize: '10px', fontWeight: 600, padding: '2px 6px', borderRadius: '4px',
                          background: '#dcfce7', color: '#15803d',
                        }}>Signed</span>
                      ) : (
                        <button
                          className="btn btn-sm"
                          onClick={() => openDocForm(venue)}
                          disabled={isBusy(venue.id)}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                            fontSize: '11px', padding: '3px 8px',
                            background: venue.msaStatus === 'generated' ? 'var(--gray-100)' : 'var(--secondary-light)',
                            color: venue.msaStatus === 'generated' ? 'var(--gray-600)' : 'var(--secondary)',
                            border: `1px solid ${venue.msaStatus === 'generated' ? 'var(--gray-300)' : 'var(--secondary)'}`,
                            borderRadius: '6px', cursor: 'pointer', fontWeight: 600,
                          }}
                        >
                          <DocIcon />
                          {generatingDocs === venue.id ? 'Generating...' : venue.msaStatus === 'generated' ? 'Regenerate' : 'Generate'}
                        </button>
                      )}
                    </td>

                    {/* Actions */}
                    <td>
                      {selectedVenueId === venue.id ? (
                        <span style={{ fontSize: '13px', color: 'var(--primary)', fontWeight: 600 }}>Current</span>
                      ) : (
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleSwitchVenue(venue)}
                          disabled={switching === venue.id}
                        >
                          {switching === venue.id ? 'Switching...' : 'Sign In'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '16px 0 0 0', borderTop: '1px solid var(--gray-200)', marginTop: '16px'
              }}>
                <span style={{ fontSize: '14px', color: 'var(--gray-600)' }}>
                  Showing {(pagination.page - 1) * pagination.limit + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} venues
                </span>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button className="btn btn-sm btn-secondary" onClick={() => handlePageChange(pagination.page - 1)} disabled={pagination.page <= 1}>Previous</button>
                  <span style={{ padding: '8px 12px', fontSize: '14px' }}>Page {pagination.page} of {pagination.totalPages}</span>
                  <button className="btn btn-sm btn-secondary" onClick={() => handlePageChange(pagination.page + 1)} disabled={pagination.page >= pagination.totalPages}>Next</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div style={{ marginTop: '24px', padding: '16px', background: 'var(--gray-100)', borderRadius: '8px' }}>
        <p style={{ fontSize: '12px', color: 'var(--gray-500)', margin: 0 }}>
          <strong>Tip:</strong> Click "Sign In" to switch to any venue's context for setup, debugging, or manual configuration.
          The page will reload with the new venue's data.
        </p>
      </div>
      {/* Generate Docs Modal */}
      {docFormVenue && (
        <div className="confirm-modal-overlay" onClick={() => !docFormSubmitting && setDocFormVenue(null)}>
          <div className="confirm-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px', width: '100%' }}>
            <div className="confirm-modal-icon confirm-modal-icon-info">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
            </div>
            <div className="confirm-modal-content">
              <h3 className="confirm-modal-title">Generate MSA Documents</h3>
              <p style={{ fontSize: '13px', color: 'var(--gray-500)', margin: '0 0 16px' }}>
                Review the fields below before generating documents for <strong>{docFormVenue.name}</strong>.
                {docFormVenue.msaStatus === 'generated' && (
                  <span style={{ color: 'var(--warning)', display: 'block', marginTop: '6px' }}>
                    This will replace the existing unsigned documents.
                  </span>
                )}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', textAlign: 'left' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--gray-600)', marginBottom: '4px' }}>Legal Entity Name</label>
                  <input
                    className="form-input"
                    value={docFormData.legalName}
                    onChange={e => setDocFormData(d => ({ ...d, legalName: e.target.value }))}
                    placeholder={docFormVenue.name}
                    style={{ width: '100%', padding: '8px 12px', fontSize: '14px', border: '1px solid var(--gray-300)', borderRadius: '8px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--gray-600)', marginBottom: '4px' }}>
                    Contact Name <span style={{ color: 'var(--danger)' }}>*</span>
                  </label>
                  <input
                    className="form-input"
                    value={docFormData.contactName}
                    onChange={e => setDocFormData(d => ({ ...d, contactName: e.target.value }))}
                    placeholder="Name of person signing the agreement"
                    style={{ width: '100%', padding: '8px 12px', fontSize: '14px', border: `1px solid ${!docFormData.contactName.trim() ? 'var(--danger)' : 'var(--gray-300)'}`, borderRadius: '8px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--gray-600)', marginBottom: '4px' }}>
                    Contact Email <span style={{ color: 'var(--danger)' }}>*</span>
                  </label>
                  <input
                    className="form-input"
                    type="email"
                    value={docFormData.contactEmail}
                    onChange={e => setDocFormData(d => ({ ...d, contactEmail: e.target.value }))}
                    placeholder="support@picklecourts.rent"
                    style={{ width: '100%', padding: '8px 12px', fontSize: '14px', border: `1px solid ${!docFormData.contactEmail.trim() ? 'var(--danger)' : 'var(--gray-300)'}`, borderRadius: '8px' }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--gray-600)', marginBottom: '4px' }}>Display Name</label>
                    <input
                      className="form-input"
                      value={docFormData.displayName}
                      disabled
                      style={{ width: '100%', padding: '8px 12px', fontSize: '14px', border: '1px solid var(--gray-200)', borderRadius: '8px', background: 'var(--gray-50)', color: 'var(--gray-500)' }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--gray-600)', marginBottom: '4px' }}>Commission</label>
                    <input
                      className="form-input"
                      value={docFormData.commissionBps ? `${docFormData.commissionBps}%` : 'Not set'}
                      disabled
                      style={{ width: '100%', padding: '8px 12px', fontSize: '14px', border: '1px solid var(--gray-200)', borderRadius: '8px', background: 'var(--gray-50)', color: 'var(--gray-500)' }}
                    />
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--gray-600)', marginBottom: '4px' }}>Location</label>
                  <input
                    className="form-input"
                    value={docFormData.address}
                    disabled
                    style={{ width: '100%', padding: '8px 12px', fontSize: '14px', border: '1px solid var(--gray-200)', borderRadius: '8px', background: 'var(--gray-50)', color: 'var(--gray-500)' }}
                  />
                </div>
              </div>
              {docFormError && (
                <p style={{ fontSize: '13px', color: 'var(--danger)', margin: '12px 0 0', fontWeight: 500 }}>{docFormError}</p>
              )}
            </div>
            <div className="confirm-modal-actions">
              <button className="btn btn-secondary" onClick={() => setDocFormVenue(null)} disabled={docFormSubmitting}>Cancel</button>
              <button className="btn btn-primary" onClick={submitDocForm} disabled={docFormSubmitting || !docFormData.contactName.trim() || !docFormData.contactEmail.trim()}>
                {docFormSubmitting ? <span className="confirm-modal-spinner" /> : 'Generate Documents'}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
